import axiosInstance from '../../core/axiosConfig';

export interface DotykackaStatus {
  connected: boolean;
  cloudId: string | null;
  branchId: string | null;
  branchName: string | null;
  /** false when connected but Dotypos could not be queried (e.g. access revoked). */
  reachable: boolean | null;
  /** Dotypos payment method the till issues receipts with; null leaves open bills. */
  paymentMethodId: number | null;
}

/**
 * Ids from Dotypos' payment methods enum (docs.api.dotypos.com/api-reference/enums/payment-methods).
 * Kept below Number.MAX_SAFE_INTEGER, unlike product ids.
 */
export const DOTYKACKA_PAYMENT_METHODS = [
  { id: 900000019, labelKey: 'dotykacka.paymentMethods.online' },
  { id: 900000002, labelKey: 'dotykacka.paymentMethods.card' },
  { id: 900000009, labelKey: 'dotykacka.paymentMethods.transfer' },
] as const;

/** Till ids stay strings: they exceed the integer precision of a JavaScript number. */
export interface DotykackaBranch {
  id: string;
  name: string;
}

export interface DotykackaProduct {
  id: string;
  name: string;
  priceWithVat: number | null;
}

export interface DotykackaProductMapping {
  inventoryItemId: number;
  name: string;
  price: number | null;
  posId: string | null;
}

export interface DotykackaConnectForm {
  actionUrl: string;
  fields: Record<string, string>;
}

export class DotykackaService {
  async getStatus(locationId: number | string): Promise<DotykackaStatus> {
    const response = await axiosInstance.get('/dotykacka/status', { params: { locationId } });
    return response.data;
  }

  async createConnectForm(locationId: number | string): Promise<DotykackaConnectForm> {
    const response = await axiosInstance.post('/dotykacka/connect', null, { params: { locationId } });
    return response.data;
  }

  async getBranches(locationId: number | string): Promise<DotykackaBranch[]> {
    const response = await axiosInstance.get('/dotykacka/branches', { params: { locationId } });
    return response.data;
  }

  async selectBranch(locationId: number | string, branchId: string): Promise<DotykackaStatus> {
    const response = await axiosInstance.put('/dotykacka/branch', { locationId: Number(locationId), branchId });
    return response.data;
  }

  async selectPaymentMethod(locationId: number | string, paymentMethodId: number | null): Promise<DotykackaStatus> {
    const response = await axiosInstance.put('/dotykacka/payment-method', { locationId: Number(locationId), paymentMethodId });
    return response.data;
  }

  async disconnect(locationId: number | string): Promise<void> {
    await axiosInstance.delete('/dotykacka/connection', { params: { locationId } });
  }

  async getProducts(locationId: number | string): Promise<DotykackaProduct[]> {
    const response = await axiosInstance.get('/dotykacka/products', { params: { locationId } });
    return response.data;
  }

  async getProductMapping(locationId: number | string): Promise<DotykackaProductMapping[]> {
    const response = await axiosInstance.get('/dotykacka/product-mapping', { params: { locationId } });
    return response.data;
  }

  async saveProductMapping(
    locationId: number | string,
    items: { inventoryItemId: number; posId: string | null }[],
  ): Promise<DotykackaProductMapping[]> {
    const response = await axiosInstance.put('/dotykacka/product-mapping', { locationId: Number(locationId), items });
    return response.data;
  }
}

/**
 * Dotypos accepts the connector only as a form POST from the browser, and rejects a
 * timestamp older than about a minute, so this must run right after createConnectForm.
 */
export const submitConnectForm = ({ actionUrl, fields }: DotykackaConnectForm): void => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = actionUrl;

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};
